import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getPartnerWhatsappConversations } from "../../api/partnerPortalApi";
import PartnerWhatsappConversationTable from "../../components/partner/whatsapp/PartnerWhatsappConversationTable";
import PartnerWhatsappExportButton from "../../components/partner/whatsapp/PartnerWhatsappExportButton";
import { usePartnerPortalBase } from "../../components/partner/PartnerPortalBaseProvider";
import {
  PARTNER_WHATSAPP_OUTCOME_FILTERS,
  PARTNER_WHATSAPP_RANGE_OPTIONS,
  PARTNER_WHATSAPP_STATUS_FILTERS,
} from "../../constants/partnerWhatsappMonitoringConstants";
import {
  buildConversationListSearchParams,
  conversationHasActiveFilters,
  parseConversationListSearchParams,
} from "../../utils/partnerWhatsappConversationsViewModel";
import { exportPartnerWhatsappConversationsCsv } from "../../utils/partnerWhatsappExport";
import "../../styles/partnerPortalDashboard.css";

function ConversationsSkeleton() {
  return (
    <div className="partner-dashboard partner-dashboard-skeleton" aria-busy="true">
      <div className="partner-skeleton-block partner-skeleton-hero" />
      <div className="partner-skeleton-block" style={{ minHeight: "18rem" }} />
    </div>
  );
}

export default function PartnerWhatsappConversationsPage() {
  const { path } = usePartnerPortalBase();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseConversationListSearchParams(searchParams), [searchParams]);
  const conversationsBasePath = path("whatsapp/conversations");
  const dealerHealthBasePath = path("whatsapp/dealers");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchInput, setSearchInput] = useState(filters.search);

  const loadConversations = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await getPartnerWhatsappConversations(filters);
      setData(body);
    } catch {
      setData(null);
      setErrorMessage("We could not load WhatsApp conversations right now.");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    setSearchInput(filters.search);
  }, [filters.search]);

  const updateFilters = (patch) => {
    const nextFilters = { ...filters, ...patch, page: patch.page ?? 1 };
    setSearchParams(buildConversationListSearchParams(nextFilters), { replace: true });
  };

  const onSearchSubmit = (event) => {
    event.preventDefault();
    updateFilters({ search: searchInput.trim() });
  };

  const dealerOptions = data?.dealerOptions?.length
    ? data.dealerOptions
    : [];
  const hasActiveFilters = conversationHasActiveFilters(filters);

  if (loading) {
    return <ConversationsSkeleton />;
  }

  if (errorMessage) {
    return (
      <div className="partner-panel partner-whatsapp-page">
        <header className="partner-dashboard-hero">
          <div>
            <h2>WhatsApp Conversations</h2>
            <p className="partner-dashboard-hero__intro">
              Review brokered WhatsApp lead activity for your dealers.
            </p>
          </div>
        </header>
        <div className="partner-panel">
          <p className="admin-data-health-error">{errorMessage}</p>
        </div>
      </div>
    );
  }

  const pagination = data?.pagination || { page: 1, pageSize: 25, total: 0, totalPages: 0 };

  return (
    <div className="partner-dashboard partner-whatsapp-page">
      <header className="partner-dashboard-hero">
        <div>
          <p className="partner-dashboard-hero__eyebrow">
            <Link to={path("whatsapp")}>WhatsApp Leads</Link>
          </p>
          <h2>WhatsApp Conversations</h2>
          <p className="partner-dashboard-hero__intro">
            Review brokered WhatsApp lead activity for your dealers.
          </p>
        </div>
        <div className="partner-whatsapp-controls">
          <PartnerWhatsappExportButton
            label="Export conversations CSV"
            onExport={() => exportPartnerWhatsappConversationsCsv(filters)}
          />
          <button type="button" className="partner-dashboard-btn" onClick={loadConversations}>
            Refresh
          </button>
        </div>
      </header>

      <section className="partner-panel partner-whatsapp-filters">
        <form className="partner-health-toolbar" onSubmit={onSearchSubmit}>
          <label>
            Range
            <select
              value={filters.range}
              onChange={(event) => updateFilters({ range: event.target.value })}
            >
              {PARTNER_WHATSAPP_RANGE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label>
            Dealer
            <select
              value={filters.dealerId}
              onChange={(event) => updateFilters({ dealerId: event.target.value })}
            >
              <option value="">All dealers</option>
              {dealerOptions.map((dealer) => (
                <option key={dealer.id} value={dealer.id}>
                  {dealer.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            Broker status
            <select
              value={filters.leadBrokerStatus}
              onChange={(event) => updateFilters({ leadBrokerStatus: event.target.value })}
            >
              {PARTNER_WHATSAPP_STATUS_FILTERS.map((option) => (
                <option key={option.value || "all-status"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label>
            Outcome
            <select
              value={filters.outcome}
              onChange={(event) => updateFilters({ outcome: event.target.value })}
            >
              {PARTNER_WHATSAPP_OUTCOME_FILTERS.map((option) => (
                <option key={option.value || "all-outcome"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="partner-whatsapp-filter-check">
            <input
              type="checkbox"
              checked={filters.stuck}
              onChange={(event) => updateFilters({ stuck: event.target.checked })}
            />
            Stuck only
          </label>

          <label className="partner-whatsapp-filter-check">
            <input
              type="checkbox"
              checked={filters.manualOutreachRequired}
              onChange={(event) => updateFilters({ manualOutreachRequired: event.target.checked })}
            />
            Manual outreach only
          </label>

          <label>
            Search
            <input
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Lead ref, dealer, vehicle"
            />
          </label>

          <button type="submit" className="partner-dashboard-btn">
            Apply
          </button>
        </form>
      </section>

      <section className="partner-panel partner-health-table-panel">
        {!data?.items?.length ? (
          <div className="partner-health-empty">
            <h3>No WhatsApp conversations found.</h3>
            <p className="muted">
              {hasActiveFilters
                ? "Try clearing filters or changing the date range."
                : "When buyers submit WhatsApp leads for your dealers, conversations will appear here."}
            </p>
          </div>
        ) : (
          <PartnerWhatsappConversationTable
            items={data.items}
            conversationsBasePath={conversationsBasePath}
            dealerHealthBasePath={dealerHealthBasePath}
          />
        )}

        {pagination.totalPages > 1 ? (
          <div className="partner-health-pagination">
            <span className="muted">
              Page {pagination.page} of {pagination.totalPages} · {pagination.total} conversations
            </span>
            <div className="partner-whatsapp-controls">
              <button
                type="button"
                className="partner-dashboard-btn"
                disabled={pagination.page <= 1}
                onClick={() => updateFilters({ page: pagination.page - 1 })}
              >
                Previous
              </button>
              <button
                type="button"
                className="partner-dashboard-btn"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => updateFilters({ page: pagination.page + 1 })}
              >
                Next
              </button>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}
